import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import {
  getCurrentSiteFromLocalStorage,
  getUserResources,
  setCurrentSiteInLocalStorage,
  USER_RESOURCES_QUERY_CONFIG,
  USER_RESOURCES_QUERY_KEY,
} from '@hotjar/user-resources';
import type { UserResources } from '@hotjar/user-resources';
import { useCurrentUser } from '@hotjar/user';
import type { Site } from '../model';
import { useSiteStore } from '../siteStore';

export const currentSiteSelector =
  (siteId: number | undefined) =>
  (userResources: UserResources): Site | undefined => {
    // Use the preferredCurrentSiteId saved in localStorage if possible
    const selectedSite = userResources.sites.find((site) => site.id === siteId);
    if (selectedSite) {
      return selectedSite;
    }

    // If this does not exist (e.g. has been deleted or the
    // user no longer has access to it), use the first site in the resource list.
    if (userResources.sites && userResources.sites.length > 0) {
      return userResources.sites[0];
    }

    return undefined;
  };

export const useCurrentSite = (): UseQueryResult<Site | undefined, Error> => {
  const preferredCurrentSiteId = useSiteStore((state) => state.preferredCurrentSiteId);

  const { data } = useCurrentUser();
  const userId = data?.id;

  return useQuery(USER_RESOURCES_QUERY_KEY, () => getUserResources(userId as number), {
    enabled: !!userId,
    select: currentSiteSelector(preferredCurrentSiteId),
    placeholderData: getCurrentSiteFromLocalStorage,
    onSuccess: setCurrentSiteInLocalStorage,
    ...USER_RESOURCES_QUERY_CONFIG,
  });
};

export const sitePropSelector =
  <T>(siteId: number | undefined, selector: (site: Site | undefined) => T) =>
  (userResources: UserResources): T | undefined => {
    const currentSite = currentSiteSelector(siteId)(userResources);
    if (currentSite) {
      return selector(currentSite);
    }
    return undefined;
  };

/**
 * Intended for temporary use until we get specific APIs.
 * This can be used within other hooks that rely on the current site to return data
 * for example: currentOrganization or currentAccount.
 */
export const useCurrentSiteProp = <T>(
  selector: (site: Site | undefined) => T,
): UseQueryResult<T | undefined, Error> => {
  const preferredCurrentSiteId = useSiteStore((state) => state.preferredCurrentSiteId);
  const { data } = useCurrentUser();
  const userId = data?.id;
  return useQuery(USER_RESOURCES_QUERY_KEY, () => getUserResources(userId as number), {
    enabled: !!userId,
    select: sitePropSelector(preferredCurrentSiteId, selector),
    placeholderData: () => selector(getCurrentSiteFromLocalStorage()),
    ...USER_RESOURCES_QUERY_CONFIG,
  });
};
